import { useEffect, useState } from 'react';
import { genQrImage } from '@tapple.io/qr-code-generator';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { useAuthStore } from '@/lib/core/stores/useAuthStore';
import { useMemberCardStore } from '@/lib/core/stores/useMemberCardStore';
import { ImageMap } from '@/lib/core/utils';
import { useClientTranslation } from '@/lib/core';
import { useApp } from '@/lib/core/providers/appProvider';
import { Icon } from '@/components/ui/icon';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import {
  ApiBlockListModel,
  MemberTypeBackgroundPropertiesModel,
  MembershipDto,
} from '@/services/platform/api/model';
import { getMembership } from '@/services/platform/data/profile';

const MODAL_ID = 'member-card';

function resolveBackgroundUrl(blocks: ApiBlockListModel | undefined, memberType: string): string | null {
  if (!blocks?.items?.length) return null;

  const normalised = memberType.toLowerCase();
  const match = blocks.items.find(item => {
    const props = item?.content?.properties as MemberTypeBackgroundPropertiesModel | undefined;
    const ref = props?.memberType?.[0];
    return ref?.name?.toLowerCase() === normalised;
  });

  const block = match ?? blocks.items[0];
  const props = block?.content?.properties as MemberTypeBackgroundPropertiesModel | undefined;
  if (!props?.backgroundImage?.length) return null;

  return ImageMap(props.backgroundImage[0])?.src ?? null;
}

export function MemberCardSheet() {
  const { activeModal, closeModal } = useUIStore();
  const { cmsProperties } = useMemberCardStore();
  const { user } = useAuthStore();
  const { memberTheme } = useApp();
  const translation = useClientTranslation();
  const isOpen = activeModal === MODAL_ID;

  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [membership, setMembership] = useState<MembershipDto | undefined>(undefined);

  useEffect(() => {
    getMembership().then(setMembership);
  }, []);

  const title = cmsProperties?.titleHeading ?? 'YOUR MEMBER CARD';
  const scanLabel =
    cmsProperties?.scanLabel ?? 'Scan this QR Code at JNcQUOI venues and events to gain access';

  const memberName = user?.name ?? '';
  const memberType = membership?.type ?? '';
  const cardUrl = membership?.cardUrl ?? null;
  const cardId = membership?.cardId ?? null;
  const qrSource = cardUrl ?? cardId ?? null;

  const cmsBackgroundUrl = resolveBackgroundUrl(cmsProperties?.memberTypeBackgrounds, memberType);

  useEffect(() => {
    if (!isOpen || !qrSource) {
      setQrDataUrl(null);
      return;
    }
    genQrImage(qrSource, {
      size: 226,
      margin: 8,
      output: { format: 'svg', type: 'dataURL' },
    }).then(url => setQrDataUrl(url as string));
  }, [isOpen, qrSource]);

  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) closeModal();
      }}
      showSwipeHandle={false}
    >
      <DrawerContent className="h-[80dvh] min-h-[min(650px,100dvh)]">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="shrink-0">
          <div className="flex items-center justify-between px-2 pt-4 [@media(max-height:666px)]:pt-6">
            <div className="flex w-22 items-center">
              <div className="size-11" />
            </div>

            <p className="font-pp-neue-montreal shrink-0 text-[14px] leading-none font-semibold tracking-[0.5px] text-[#1a1a1a] uppercase">
              {title}
            </p>

            <div className="flex w-22 justify-end">
              <button
                type="button"
                className="flex size-11 cursor-pointer items-center justify-center rounded-full p-2.5"
                aria-label={translation('App.Labels.Close')}
                onClick={closeModal}
              >
                <Icon
                  name="xmark"
                  width={24}
                  height={24}
                  strokeWidth={1.5}
                  className="text-jncquoi-neutral-1000"
                />
              </button>
            </div>
          </div>
        </div>

        {/* ── Scrollable content ──────────────────────────────────────── */}
        {/* `data-base-ui-swipe-ignore` so dragging inside scrolls instead of dismissing the drawer. */}
        <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto">
          <div className="flex min-h-full flex-col pb-8">
            {/* ── Member card ─────────────────────────────────────────────── */}
            <div className="mx-4 mt-6">
              <div
                className="rounded-jncquoi relative overflow-hidden"
                style={{ background: memberTheme.cardBg }}
              >
                <div className="flex py-6 pl-6">
                  {/* Left side: member info + logo */}
                  <div className="flex shrink-0 flex-col gap-7">
                    <div className="flex flex-col gap-2 whitespace-nowrap">
                      <p className="font-pp-neue-montreal text-[16px] leading-none font-normal tracking-[0.5px] text-white">
                        {memberName}
                      </p>
                      <p className="font-pp-neue-montreal text-[16px] leading-none font-normal tracking-[0.5px] text-[#c3ab83]">
                        {memberType}
                      </p>
                    </div>

                    {/* Logo — mix-blend-hard-light so it reads well on dark card */}
                    <div className="h-15 w-15 mix-blend-hard-light">
                      <img
                        src="/images/logo/jncquoi-c-white.svg"
                        alt={translation('App.Labels.MemberCardLogo')}
                        className="size-full object-contain mix-blend-lighten"
                      />
                    </div>
                  </div>

                  {/* Right side: background image from Umbraco */}
                  {cmsBackgroundUrl && (
                    <img
                      src={cmsBackgroundUrl}
                      alt="Member Type Background"
                      aria-hidden="true"
                      className="ml-auto max-h-40 w-53.75 shrink self-stretch object-cover"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* ── QR code + caption ───────────────────────────────────────── */}
            <div className="mt-12 flex flex-col items-center gap-6">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt={translation('App.Labels.MemberQRCode')}
                  width={226}
                  height={226}
                  className="shrink-0"
                />
              ) : (
                <div className="size-56.5 animate-pulse rounded bg-neutral-100" />
              )}

              <p className="font-pp-neue-montreal w-56.5 text-center text-[14px] leading-normal font-normal tracking-[0.5px] text-[#1a1a1a]">
                {scanLabel}
              </p>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
